import type { NoyauTextsDraft } from '@/lib/noyau-texts-draft';
import type { VersionedBrowserDraft } from '@/lib/versioned-draft';
import { noyauTextsDraftContentSchema } from '@matter_extractor/shared';
import { z } from 'zod';

/**
 * The entry of Réglages > Noyaux typed BEFORE the v1, kept in this browser
 * (noyau.md §6.4, N7, N8; review of !462).
 *
 * The specialist delivers the eighteen texts a few at a time, and « le
 * brouillon autosauvé absorbe une saisie partielle » (N7) is the promise that
 * seven of them typed on Monday are still there on Thursday. After the v1 that
 * is the server draft of the layer. Before it, there is no entity to hold one -
 * the singleton is born on its first save (N8), and `development_version_id`
 * is never null - so without this module a tab switch or a reload lost the
 * seven texts without a word.
 *
 * The régime of `lib/session-memory.ts` and `lib/chat-outbox.ts`: `localStorage`
 * because a React state dies at the `F5` this exists to survive; a key PER
 * OPERATOR, because two people can share a browser and each types their own
 * page; what is read back crosses a trust boundary, so it is parsed - the
 * wrapper here, the content by `noyauTextsDraftContentSchema`, the schema the
 * server's draft route validates with - and a value nobody can read is an
 * empty entry, never a throw; and EVERY access in a `try`, because a private
 * window or blocked site data throws on access instead of answering null, and
 * the page must render the same without any of this.
 *
 * The layer makes the three calls, only while no version exists
 * (`VersionedBrowserDraft`): the read once when the editor opens, the write at
 * the rhythm of the autosave, the clear on the first save that landed.
 */

const PREFIX = 'matter-extractor:noyau-texts:brouillon:';

/**
 * The key: one per operator. `null` is the SHARED token of the machines (the
 * e2e suites, the captures), which names no person - it gets one key of its
 * own rather than none.
 */
export function noyauTextsBrowserDraftKey(email: string | null): string {
  return `${PREFIX}${email ?? 'jeton-partage'}`;
}

/** What this module needs of a store: `localStorage`, or an in-memory stand-in in a test. */
export interface NoyauTextsDraftStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** The stored envelope; its `content` is parsed on its own, by the draft's schema. */
const keptEnvelopeSchema = z.object({
  keptAt: z.string().datetime(),
  content: z.unknown(),
});

/** What this browser kept for this key, or null for anything it cannot read. */
export function readKeptNoyauTexts(
  storage: NoyauTextsDraftStorage,
  key: string,
): { readonly draft: NoyauTextsDraft; readonly keptAt: string } | null {
  let raw: string | null;
  try {
    raw = storage.getItem(key);
  } catch {
    return null;
  }
  if (raw === null) return null;
  try {
    const envelope = keptEnvelopeSchema.safeParse(JSON.parse(raw));
    if (!envelope.success) return null;
    const content = noyauTextsDraftContentSchema.safeParse(envelope.data.content);
    return content.success ? { draft: content.data, keptAt: envelope.data.keptAt } : null;
  } catch {
    // Not JSON at all: written by nobody this code knows.
    return null;
  }
}

/**
 * Keep the entry: the instant it was kept, or null when the browser refused -
 * a full quota, a private window. Swallowed, with the refusal returned: the
 * head says so, and the typing still lives in the page for as long as it is
 * open.
 */
export function keepNoyauTexts(
  storage: NoyauTextsDraftStorage,
  key: string,
  draft: NoyauTextsDraft,
  now: Date,
): string | null {
  const keptAt = now.toISOString();
  try {
    storage.setItem(key, JSON.stringify({ keptAt, content: draft }));
    return keptAt;
  } catch {
    return null;
  }
}

/** Forget the entry - the first save made it a version, or a version already exists. */
export function forgetKeptNoyauTexts(storage: NoyauTextsDraftStorage, key: string): void {
  try {
    storage.removeItem(key);
  } catch {
    // Nothing to forget in a browser that keeps nothing.
  }
}

/** The browser's own store, or null where merely touching it throws. */
function browserStorage(): NoyauTextsDraftStorage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** The three calls the layer makes, bound to this operator and to this browser. */
export function noyauTextsBrowserDraft(
  email: string | null,
  storage: NoyauTextsDraftStorage | null = browserStorage(),
): VersionedBrowserDraft<NoyauTextsDraft> {
  const key = noyauTextsBrowserDraftKey(email);
  return {
    read: () => (storage === null ? null : readKeptNoyauTexts(storage, key)),
    write: (draft) => (storage === null ? null : keepNoyauTexts(storage, key, draft, new Date())),
    clear: () => {
      if (storage !== null) forgetKeptNoyauTexts(storage, key);
    },
  };
}
